import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { Teleprompter } from '@/components/Teleprompter';
import { AvatarIdleGuidance, AvatarIdlePeriod, AvatarTypeToRecordingTimes } from '@/pages/avatar/common/consts';
import { useCreateAvatarStore } from '@/pages/avatar/common/context';
import theme from '@/theme';
import styled from '@emotion/styled';
import CloseIcon from '@mui/icons-material/Close';
import { DialogContent, DialogTitle } from '@mui/material';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { SelectScript } from './SelectScript';

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        display: flex;
        flex-direction: column;
        gap: 16px;
        overflow: visible;
        padding: 0 24px 24px;
        width: 720px;
    }
`;

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        display: flex;
        gap: 8px;

        padding: 24px;
        color: ${theme.colors.secondary[900]};
        font-size: 24px;
        font-weight: 700;
        letter-spacing: -0.48px;
    }
`;

const CloseButton = styled(IconButton)`
    margin-left: auto;
    color: ${theme.colors.black[800]};

    svg {
        width: 24px;
        height: 24px;
    }
`;

interface Props {
    open: boolean;
    setOpen: (open: boolean) => void;
    onFileChange: (file: File | null) => void;
}

export const ScriptModal = observer(({ open, setOpen, onFileChange }: Props) => {
    const [teleprompterOpen, setTeleprompterOpen] = useState(false);
    const [script, setScript] = useState('');
    const createAvatarStore = useCreateAvatarStore();
    const { minRecordingTime, recordingTime } = AvatarTypeToRecordingTimes[createAvatarStore.type];

    const handleClose = () => {
        setOpen(false);
    };

    const handleVideo = async (blob: Blob | null) => {
        if (!blob) {
            return onFileChange(null);
        }
        const file = new File([blob], 'video.mp4', { type: 'video/mp4' });
        onFileChange(file);
    };

    const handleScriptChange = (value: string) => {
        createAvatarStore.setUsedFootageScript(true);
        setScript(value);

        handleClose();
        setTeleprompterOpen(true);
    };

    const handleWithoutScript = () => {
        createAvatarStore.setUsedFootageScript(false);
        createAvatarStore.setFootageScriptLanguage('');
        setScript('');

        handleClose();
        setTeleprompterOpen(true);
    };

    return (
        <>
            <BootstrapDialog open={open}>
                <StyledDialogTitle>
                    Preview script
                    <CloseButton ariaLabel="close" onClick={handleClose}>
                        <CloseIcon />
                    </CloseButton>
                </StyledDialogTitle>

                <StyledDialogContent>
                    <SelectScript onScriptChange={handleScriptChange} onWithoutScript={handleWithoutScript} />
                </StyledDialogContent>
            </BootstrapDialog>
            <Teleprompter
                text={script}
                open={teleprompterOpen}
                setOpen={setTeleprompterOpen}
                onChange={handleVideo}
                minRecordingTime={minRecordingTime}
                recordingTime={recordingTime}
                avatarType={createAvatarStore.type}
                nextPageStaticText={AvatarIdleGuidance}
                lastSectionTime={AvatarIdlePeriod}
            />
        </>
    );
});
